
import { computed, defineComponent, ref, SetupContext, CSSProperties } from 'vue';
import TIcon from './../t-icon/index';

export default defineComponent({
  name: 'TCell',
  components: { TIcon },
  props: {
    hasArrow: {
      type: Boolean,
      default: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hasBottomBorder: {
      type: Boolean,
      default: true,
    },
    hasTopBorder: {
      type: Boolean,
      default: false,
    },
    isAutoHeight: {
      type: Boolean,
      default: false,
    },
    leftTitleFontface: {
      type: Boolean,
      default: false,
    },
    leftLabelFontface: {
      type: Boolean,
      default: false,
    },
    rightTitleFontface: {
      type: Boolean,
      default: false,
    },
    rightLabelFontface: {
      type: Boolean,
      default: false,
    },
    leftTitle: {
      type: String,
      default: '',
    },
    leftTitleIcon: {
      type: String,
      default: '',
    },
    leftTitleImage: {
      type: String,
      default: '',
    },
    leftLabel: {
      type: String,
      default: '',
    },
    rightTitle: {
      type: String,
      default: '',
    },
    rightTitleIcon: {
      type: String,
      default: '',
    },
    rightTitleImage: {
      type: String,
      default: '',
    },
    rightLabel: {
      type: String,
      default: '',
    },
    iconName: {
      type: String,
      default: 'more',
    },
    leftWidth: {
      type: Number,
      default: 0,
    },
    rightWidth: {
      type: Number,
      default: 0,
    },
    gutter: {
      type: Number,
      default: 0,
    },
    badge: {
      type: [Number, Boolean],
      default: false,
    },
    theme: {
      type: String,
      default: 'default',
      validator: (value: string) => ['default', 'error', 'success', 'warning'].includes(value),
    },
    align: {
      type: String,
      default: 'center',
      validator: (value: string) => ['start', 'end', 'center'].includes(value),
    },
    textAlign: {
      type: String,
      default: 'right',
      validator: (value: string) => ['left', 'right', 'center'].includes(value),
    },
    size: {
      type: String,
      default: 'medium',
      validator: (value: string) => ['xsmall', 'small', 'medium', 'large'].includes(value),
    },
  },
  emits: ['click'],
  setup(props, context: SetupContext) {
    const prefixCls = ref('t-cell');
    /** 定义左边的宽度信息 */
    const stylesByLeft = computed(() => (props.leftWidth ? { 'max-width': `${props.leftWidth}px` } as CSSProperties : {}));
    /** 定义右边的宽度信息 */
    const stylesByRight = computed(() => (props.rightWidth ? { 'max-width': `${props.rightWidth}px` } as CSSProperties : {}));
    /** 判断是否有左侧标题图标 */
    const hasLeftTitleImage = computed(() => props.leftTitleIcon || props.leftTitleImage);
    /** 判断是否有右侧标题图标 */
    const hasRightTitleImage = computed(() => props.rightTitleIcon || props.rightTitleImage);
    /** 判断是否有文字信息 */
    const hasContent = computed(() => {
      return {
        leftTitle: props.leftTitle || context.slots.leftTitle,
        leftLabel: props.leftLabel || context.slots.leftLabel,
        rightTitle: props.rightTitle || context.slots.rightTitle,
        rightLabel: props.rightLabel || context.slots.rightLabel,
      };
    });
    /** 左边的文案信息是否只需要显示1行 */
    const isSingleLineByLeft = computed(() => {
      const { leftTitle, leftLabel, rightTitle, rightLabel } = hasContent.value;
      return (leftTitle && !leftLabel) || (!leftTitle && leftLabel);
    });
    /** 右边的文案信息是否只需要显示1行 */
    const isSingleLineByRight = computed(() => {
      const { leftTitle, leftLabel, rightTitle, rightLabel } = hasContent.value;
      return (rightTitle && !rightLabel) || (!rightTitle && rightLabel);
    });
    /** 只有左侧文案 */
    const isOnlyLeft = computed(() => {
      const { leftTitle, leftLabel, rightTitle, rightLabel } = hasContent.value;
      return (leftTitle || leftLabel) && !(rightTitle || rightLabel);
    });
    /** 只有右侧文案 */
    const isOnlyRight = computed(() => {
      const { leftTitle, leftLabel, rightTitle, rightLabel } = hasContent.value;
      return !(leftTitle || leftLabel) && (rightTitle || rightLabel);
    });
    /** 左侧文案是多行 */
    const isMultiLineByLeft = computed(() => {
      const { leftTitle, leftLabel } = hasContent.value;
      return leftTitle && leftLabel;
    });
    /** 右侧文案是多行 */
    const isMultiLineByRight = computed(() => {
      const { rightTitle, rightLabel } = hasContent.value;
      return rightTitle && rightLabel;
    });
    /** 两边都是多行方案的情况 */
    const isMultiLineByBoth = computed(() => isMultiLineByLeft.value && isMultiLineByRight.value);
    /** 判断badge到底是boolean还是number */
    const badgeValue = computed(() => (typeof props.badge === 'boolean' ? -1 : props.badge));
    /** 基础布局的className */
    const baseLayoutClasses = computed(() => {
      return {
        wrap: [prefixCls.value, !props.isAutoHeight && `${prefixCls.value}-height__${props.size}`],
        content: [`${prefixCls.value}-content`],
        prefix: [`${prefixCls.value}-prefix`, props.disabled && `${prefixCls.value}__disabled`],
        cntBefore: [`${prefixCls.value}-before`, props.disabled && `${prefixCls.value}__disabled`],
        cntAfter: [`${prefixCls.value}-after`, props.disabled && `${prefixCls.value}__disabled`],
      };
    });
    const bothClasses = computed(() => {
      return {
        wrap: [`${prefixCls.value}-bd`],
        leftTitle: [
          `${prefixCls.value}-title`,
          `${prefixCls.value}-txt__left`,
          `${prefixCls.value}-title__${props.size}`,
          props.leftTitleFontface && `${prefixCls.value}-font`,
          props.disabled && `${prefixCls.value}-theme__disabled`,
        ],
        leftLabel: [
          `${prefixCls.value}-label`,
          `${prefixCls.value}-txt__left`,
          props.leftLabelFontface && `${prefixCls.value}-font`,
          props.disabled && `${prefixCls.value}-theme__disabled`,
        ],
        rightTitle: [
          `${prefixCls.value}-title`,
          `${prefixCls.value}-txt__right`,
          `${prefixCls.value}-title__${props.size}`,
          props.rightTitleFontface && `${prefixCls.value}-font`,
          props.disabled && `${prefixCls.value}-theme__disabled`,
        ],
        rightLabel: [
          `${prefixCls.value}-label`,
          `${prefixCls.value}-txt__right`,
          `${prefixCls.value}-theme__${props.theme}`,
          props.rightLabelFontface && `${prefixCls.value}-font`,
          props.disabled && `${prefixCls.value}-theme__disabled`,
        ],
      };
    });
    const onlyClasses = computed(() => {
      return {
        wrap: [`${prefixCls.value}-bd`],
        leftTitle: [
          `${prefixCls.value}-title`,
          `${prefixCls.value}-txt__left`,
          `${prefixCls.value}-title__${props.size}`,
          props.leftTitleFontface && `${prefixCls.value}-font`,
          isSingleLineByLeft.value && `${prefixCls.value}-title__single`,
          props.disabled && `${prefixCls.value}-theme__disabled`,
        ],
        leftLabel: [
          `${prefixCls.value}-label`,
          `${prefixCls.value}-txt__left`,
          `${prefixCls.value}-theme__${props.theme}`,
          props.leftLabelFontface && `${prefixCls.value}-font`,
          isSingleLineByLeft.value && `${prefixCls.value}-label__single`,
          props.disabled && `${prefixCls.value}-theme__disabled`,
        ],
        rightTitle: [
          `${prefixCls.value}-title`,
          `${prefixCls.value}-txt__right`,
          `${prefixCls.value}-title__${props.size}`,
          props.rightTitleFontface && `${prefixCls.value}-font`,
          isSingleLineByRight.value && `${prefixCls.value}-title__single`,
          props.disabled && `${prefixCls.value}-theme__disabled`,
        ],
        rightLabel: [
          `${prefixCls.value}-label`,
          `${prefixCls.value}-txt__right`,
          `${prefixCls.value}-theme__${props.theme}`,
          props.rightLabelFontface && `${prefixCls.value}-font`,
          isSingleLineByRight.value && `${prefixCls.value}-label__single`,
          props.disabled && `${prefixCls.value}-theme__disabled`,
        ],
      };
    });
    const multiClasses = computed(() => {
      return {
        wrap: [`${prefixCls.value}-row`, `${prefixCls.value}-vlign__${props.align}`],
        leftTitle: [
          `${prefixCls.value}-title`,
          `${prefixCls.value}-txt__left`,
          `${prefixCls.value}-title__${props.size}`,
          props.leftTitleFontface && `${prefixCls.value}-font`,
          isSingleLineByLeft.value && `${prefixCls.value}-title__single`,
          props.disabled && `${prefixCls.value}-theme__disabled`,
        ],
        leftLabel: [
          `${prefixCls.value}-label`,
          `${prefixCls.value}-txt__left`,
          props.leftLabelFontface && `${prefixCls.value}-font`,
          isSingleLineByLeft.value && `${prefixCls.value}-label__single`,
          props.disabled && `${prefixCls.value}-theme__disabled`,
        ],
        rightTitle: [
          `${prefixCls.value}-title`,
          `${prefixCls.value}-txt__right`,
          `${prefixCls.value}-title__${props.size}`,
          props.rightTitleFontface && `${prefixCls.value}-font`,
          isSingleLineByRight.value && `${prefixCls.value}-title__single`,
          props.disabled && `${prefixCls.value}-theme__disabled`,
        ],
        rightLabel: [
          `${prefixCls.value}-label`,
          `${prefixCls.value}-txt__right`,
          `${prefixCls.value}-theme__${props.theme}`,
          props.rightLabelFontface && `${prefixCls.value}-font`,
          isSingleLineByRight.value && `${prefixCls.value}-label__single`,
          props.disabled && `${prefixCls.value}-theme__disabled`,
        ],
      };
    });

    /** 对外响应的事件 */
    const listener = (event: any) => context.emit('click', event);

    return {
      prefixCls,
      listener,
      stylesByLeft,
      stylesByRight,
      hasLeftTitleImage,
      hasRightTitleImage,
      hasContent,
      isSingleLineByLeft,
      isSingleLineByRight,
      isOnlyLeft,
      isOnlyRight,
      isMultiLineByLeft,
      isMultiLineByRight,
      isMultiLineByBoth,
      badgeValue,
      baseLayoutClasses,
      bothClasses,
      onlyClasses,
      multiClasses,
    };
  },
});
